import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons';
import API from '../api/API';
import { User } from '../config/Registration';
import { RootStoreContext } from '../stores/RootStore';
import UserAvatar from './UserAvatar';
import './UserProfileModal.css';

interface UserProfileModalProps {
  show: boolean;
  onHide: () => void;
  onSaved?: (message: string) => void;
  user: User;
}

interface ProfileFormState {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthMonth: string;
  birthDay: string;
  birthYear: string;
  gender: string;
}

const emptyForm: ProfileFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  birthMonth: '',
  birthDay: '',
  birthYear: '',
  gender: '',
};

const MAX_AVATAR_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_SOURCE_AVATAR_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const AVATAR_TOO_LARGE_MESSAGE = 'Profile picture must be 5 MB or smaller.';
const AVATAR_SOURCE_TOO_LARGE_MESSAGE = 'Profile picture must be 25 MB or smaller.';
const AVATAR_SIZE_PX = 512;
const AVATAR_CONTENT_TYPE = 'image/jpeg';
const AVATAR_JPEG_QUALITY = 0.88;
const DIRECT_UPLOAD_MAX_RETRIES = 2;
const SUPPORTED_AVATAR_SOURCE_TYPES = ['image/png', 'image/jpeg', 'image/heic', 'image/heif'];
const MIN_BIRTH_YEAR = 1901;
const MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

interface AvatarUploadUrlResponse {
  uploadUrl: string;
  uploadToken: string;
  method: 'PUT';
  expiresAt: string;
  headers: { [key: string]: string };
  maxBytes: number;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ show, onHide, onSaved, user }) => {
  const store = useContext(RootStoreContext);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const phoneOtpInputRef = useRef<HTMLInputElement | null>(null);
  const emailOtpInputRef = useRef<HTMLInputElement | null>(null);
  const profileAlertRef = useRef<HTMLDivElement | null>(null);
  const modalBodyRef = useRef<HTMLDivElement | null>(null);
  const [form, setForm] = useState<ProfileFormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSendingPhoneCode, setIsSendingPhoneCode] = useState(false);
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState('');
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState('');
  const [phoneVerificationBypassedFor, setPhoneVerificationBypassedFor] = useState('');
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [resendSeconds, setResendSeconds] = useState(0);
  const [originalEmail, setOriginalEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [emailVerificationBypassedFor, setEmailVerificationBypassedFor] = useState('');
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailResendSeconds, setEmailResendSeconds] = useState(0);

  useEffect(() => {
    if (!show || !user?.id) {
      return;
    }

    setError(null);
    setSuccess(null);
    setForm(fromUser(user));
    setOriginalPhoneNumber(user.phoneNumber || '');
    setPendingPhoneNumber('');
    setPhoneVerificationBypassedFor('');
    setPhoneOtpCode('');
    setResendSeconds(0);
    setOriginalEmail(user.email || '');
    setPendingEmail('');
    setEmailVerificationBypassedFor('');
    setEmailOtpCode('');
    setEmailResendSeconds(0);

    API.get(`/profile/${user.id}`)
      .then((response) => {
        const profile = JSON.parse(response);
        store.registrationStore.updateUser(profile);
        setForm(fromUser(profile));
        setOriginalPhoneNumber(profile.phoneNumber || '');
        setOriginalEmail(profile.email || '');
      })
      .catch(() => {
        setError('Profile could not be loaded.');
      });
  }, [show, user?.id]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: normalizeProfileFieldValue(name, value) }));
    setError(null);
    setSuccess(null);
    if (name === 'phoneNumber' && value !== pendingPhoneNumber) {
      setPendingPhoneNumber('');
      setPhoneVerificationBypassedFor('');
      setPhoneOtpCode('');
      setResendSeconds(0);
    }
    if (name === 'email' && value !== pendingEmail) {
      setPendingEmail('');
      setEmailVerificationBypassedFor('');
      setEmailOtpCode('');
      setEmailResendSeconds(0);
    }
  };

  useEffect(() => {
    if (resendSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setResendSeconds((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [resendSeconds]);

  useEffect(() => {
    if (pendingPhoneNumber === form.phoneNumber) {
      phoneOtpInputRef.current?.focus();
    }
  }, [pendingPhoneNumber, form.phoneNumber]);

  useEffect(() => {
    if (emailResendSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setEmailResendSeconds((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [emailResendSeconds]);

  useEffect(() => {
    if (pendingEmail === form.email) {
      emailOtpInputRef.current?.focus();
    }
  }, [pendingEmail, form.email]);

  useEffect(() => {
    if (!error) {
      return;
    }

    modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    profileAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [error]);

  const handleSendEmailCode = async () => {
    if (!user?.id || isSendingEmailCode || emailResendSeconds > 0) {
      return;
    }

    setIsSendingEmailCode(true);
    setError(null);
    setSuccess(null);

    try {
      const verificationResponse = await API.post(`/profile/${user.id}/email-verification`, { email: form.email });
      const verificationResult = JSON.parse(verificationResponse);
      if (verificationResult.verificationRequired === false) {
        setPendingEmail('');
        setEmailVerificationBypassedFor(form.email);
        setEmailOtpCode('');
        setSuccess('Email is already verified.');
        return;
      }

      setPendingEmail(form.email);
      setEmailVerificationBypassedFor('');
      setEmailOtpCode('');
      setEmailResendSeconds(60);
      setSuccess(`Verification code sent to ${form.email}.`);
    } catch (requestError: any) {
      setError(toProfileError(requestError));
    } finally {
      setIsSendingEmailCode(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!user?.id || isSendingPhoneCode || resendSeconds > 0) {
      return;
    }

    setIsSendingPhoneCode(true);
    setError(null);
    setSuccess(null);

    try {
      const verificationResponse = await API.post(`/profile/${user.id}/phone-verification`, { phoneNumber: form.phoneNumber });
      const verificationResult = JSON.parse(verificationResponse);
      if (verificationResult.verificationRequired === false) {
        setPendingPhoneNumber('');
        setPhoneVerificationBypassedFor(form.phoneNumber);
        setPhoneOtpCode('');
        setSuccess('Phone number is already verified.');
        return;
      }

      setPendingPhoneNumber(form.phoneNumber);
      setPhoneVerificationBypassedFor('');
      setPhoneOtpCode('');
      setResendSeconds(60);
      setSuccess(`Verification code sent to ${form.phoneNumber}.`);
    } catch (requestError: any) {
      setError(toProfileError(requestError));
    } finally {
      setIsSendingPhoneCode(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const sanitizedForm = {
        ...form,
        firstName: collapseInnerWhitespace(form.firstName),
        lastName: collapseInnerWhitespace(form.lastName),
      };
      setForm(sanitizedForm);

      const nameValidationError = validateProfileNames(sanitizedForm);
      if (nameValidationError) {
        setError(nameValidationError);
        return;
      }

      const birthDatePayload = toBirthDatePayload(sanitizedForm);
      if (!birthDatePayload.isValid) {
        setError(birthDatePayload.error);
        return;
      }

      const response = await API.put(`/profile/${user.id}`, {
        ...sanitizedForm,
        phoneOtpCode: pendingPhoneNumber === form.phoneNumber ? phoneOtpCode.trim() || undefined : undefined,
        emailOtpCode: pendingEmail === form.email ? emailOtpCode.trim() || undefined : undefined,
        birthMonth: birthDatePayload.birthMonth,
        birthDay: birthDatePayload.birthDay,
        birthYear: birthDatePayload.birthYear,
        gender: form.gender || null,
      });
      const result = JSON.parse(response);
      if (result.authToken) {
        store.authStore.login(result.authToken);
      }
      store.registrationStore.updateUser(result.user);
      setForm(fromUser({ ...store.registrationStore.user, ...result.user }));
      setOriginalPhoneNumber(result.user?.phoneNumber || form.phoneNumber);
      setPendingPhoneNumber('');
      setPhoneVerificationBypassedFor('');
      setPhoneOtpCode('');
      setResendSeconds(0);
      setOriginalEmail(result.user?.email || form.email);
      setPendingEmail('');
      setEmailVerificationBypassedFor('');
      setEmailOtpCode('');
      setEmailResendSeconds(0);
      onHide();
      onSaved?.('Profile updated.');
    } catch (requestError: any) {
      setError(toProfileError(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const phoneChanged = hasPhoneChanged(form.phoneNumber, originalPhoneNumber);
  const isWaitingForPhoneCode = phoneChanged && pendingPhoneNumber !== '' && pendingPhoneNumber === form.phoneNumber;
  const emailChanged = originalEmail !== '' && hasEmailChanged(form.email, originalEmail);
  const isWaitingForEmailCode = emailChanged && pendingEmail !== '' && pendingEmail === form.email;

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) {
      return;
    }

    if (file.size > MAX_SOURCE_AVATAR_FILE_SIZE_BYTES) {
      setError(AVATAR_SOURCE_TOO_LARGE_MESSAGE);
      setSuccess(null);
      event.target.value = '';
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await uploadAvatarDirectly(user.id, file);
      updateUserAvatar(result.avatarUrl);
      setSuccess('Profile picture updated.');
    } catch (directUploadError: any) {
      try {
        const result = await uploadAvatarViaBackend(user.id, file);
        updateUserAvatar(result.avatarUrl);
        setSuccess('Profile picture updated.');
      } catch (fallbackError: any) {
        setError(toProfileError(fallbackError || directUploadError));
      }
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const updateUserAvatar = (avatarUrl: string) => {
    store.registrationStore.updateUser({
      ...user,
      avatarUrl: `${avatarUrl}?v=${new Date().getTime()}`,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="profile-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body ref={modalBodyRef}>
        <div className="profile-avatar-section">
          <div className="profile-avatar-wrap">
            <UserAvatar user={store.registrationStore.user || user} size="large" />
            <button
              type="button"
              className="profile-avatar-edit"
              onClick={handleAvatarClick}
              disabled={isUploading}
              aria-label="Change profile picture"
            >
              {isUploading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faCamera} />}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/heic,image/heif,.heic,.heif"
            className="profile-avatar-input"
            onChange={handleAvatarChange}
          />
        </div>

        {error && <Alert ref={profileAlertRef} variant="danger" className="profile-alert">{error}</Alert>}
        {success && <Alert variant="success" className="profile-alert">{success}</Alert>}

        <Form className="profile-form">
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>First name</Form.Label>
              <Form.Control name="firstName" value={form.firstName} onChange={handleChange} required />
            </Col>
            <Col md={6}>
              <Form.Label>Last name</Form.Label>
              <Form.Control name="lastName" value={form.lastName} onChange={handleChange} required />
            </Col>
            <Col md={6}>
              <Form.Label>Email</Form.Label>
              <div className="profile-phone-row">
                <Form.Control name="email" type="email" value={form.email} onChange={handleChange} required />
                {emailChanged && !isWaitingForEmailCode && emailVerificationBypassedFor !== form.email && (
                  <button
                    type="button"
                    className="profile-verify-button"
                    onClick={handleSendEmailCode}
                    disabled={isSaving || isUploading || isSendingPhoneCode || isSendingEmailCode}
                  >
                    {isSendingEmailCode ? <Spinner animation="border" size="sm" /> : 'Verify'}
                  </button>
                )}
              </div>
              {emailChanged && !isWaitingForEmailCode && emailVerificationBypassedFor !== form.email && (
                <div className="profile-field-note">Verify this email before saving.</div>
              )}
            </Col>
            {isWaitingForEmailCode && (
              <Col md={12}>
                <div className="profile-phone-verification">
                  <div className="profile-phone-verification-header">
                    <span>Code sent to {pendingEmail}</span>
                    <button
                      type="button"
                      className="profile-resend-button"
                      onClick={handleSendEmailCode}
                      disabled={isSendingEmailCode || emailResendSeconds > 0}
                    >
                      {emailResendSeconds > 0 ? `Resend in ${emailResendSeconds}s` : 'Resend code'}
                    </button>
                  </div>
                  <Form.Control
                    ref={emailOtpInputRef}
                    name="emailOtpCode"
                    value={emailOtpCode}
                    onChange={(event) => {
                      setEmailOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                      setError(null);
                    }}
                    className="profile-otp-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    required
                  />
                </div>
              </Col>
            )}
            <Col md={6}>
              <Form.Label>Phone number</Form.Label>
              <div className="profile-phone-row">
                <Form.Control name="phoneNumber" value={form.phoneNumber} onChange={handleChange} />
                {phoneChanged && !isWaitingForPhoneCode && phoneVerificationBypassedFor !== form.phoneNumber && (
                  <button
                    type="button"
                    className="profile-verify-button"
                    onClick={handleSendPhoneCode}
                    disabled={isSaving || isUploading || isSendingPhoneCode}
                  >
                    {isSendingPhoneCode ? <Spinner animation="border" size="sm" /> : 'Verify'}
                  </button>
                )}
              </div>
              {phoneChanged && !isWaitingForPhoneCode && phoneVerificationBypassedFor !== form.phoneNumber && (
                <div className="profile-field-note">Verify this number before saving.</div>
              )}
            </Col>
            {isWaitingForPhoneCode && (
              <Col md={12}>
                <div className="profile-phone-verification">
                  <div className="profile-phone-verification-header">
                    <span>Code sent to {pendingPhoneNumber}</span>
                    <button
                      type="button"
                      className="profile-resend-button"
                      onClick={handleSendPhoneCode}
                      disabled={isSendingPhoneCode || resendSeconds > 0}
                    >
                      {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code'}
                    </button>
                  </div>
                  <Form.Control
                    ref={phoneOtpInputRef}
                    name="phoneOtpCode"
                    value={phoneOtpCode}
                    onChange={(event) => {
                      setPhoneOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                      setError(null);
                    }}
                    className="profile-otp-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    required
                  />
                </div>
              </Col>
            )}
            <Col md={6}>
              <Form.Label>Birth date</Form.Label>
              <div className="profile-birthday-row">
                <Form.Select
                  name="birthMonth"
                  value={form.birthMonth}
                  onChange={handleChange}
                  aria-label="Birth month"
                >
                  <option value="">Month</option>
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </Form.Select>
                <Form.Control
                  name="birthDay"
                  type="number"
                  min="1"
                  max="31"
                  inputMode="numeric"
                  placeholder="Day"
                  value={form.birthDay}
                  onChange={handleChange}
                  aria-label="Birth day"
                />
                <Form.Control
                  name="birthYear"
                  type="number"
                  min={MIN_BIRTH_YEAR}
                  max={new Date().getFullYear()}
                  inputMode="numeric"
                  placeholder="Year"
                  value={form.birthYear}
                  onChange={handleChange}
                  aria-label="Birth year"
                />
              </div>
              <div className="profile-field-note">Year is optional, but adding it can unlock some perks.</div>
            </Col>
            <Col md={6}>
              <Form.Label>Gender</Form.Label>
              <Form.Select name="gender" value={form.gender} onChange={handleChange}>
                <option value="">Not set</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </Form.Select>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className="profile-modal-footer">
        <button
          type="button"
          className="profile-modal-button profile-modal-button-secondary"
          onClick={onHide}
          disabled={isSaving || isUploading || isSendingPhoneCode}
        >
          Cancel
        </button>
        <button
          type="button"
          className="profile-modal-button profile-modal-button-primary"
          onClick={handleSave}
          disabled={isSaving || isUploading || isSendingPhoneCode}
        >
          {isSaving ? <Spinner animation="border" size="sm" /> : 'Save changes'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

async function uploadAvatarDirectly(userId: number, file: File): Promise<{ avatarUrl: string }> {
  const jpegBlob = await normalizeAvatarImage(file);
  let uploadSession = await requestAvatarUploadUrl(userId, jpegBlob);
  let refreshedExpiredUrl = false;

  for (let attempt = 0; attempt <= DIRECT_UPLOAD_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(uploadSession.uploadUrl, {
        method: uploadSession.method,
        headers: uploadSession.headers,
        body: jpegBlob,
      });

      if (response.ok) {
        return completeAvatarUpload(userId, uploadSession.uploadToken);
      }

      if (response.status === 403 && !refreshedExpiredUrl) {
        uploadSession = await requestAvatarUploadUrl(userId, jpegBlob);
        refreshedExpiredUrl = true;
        attempt = -1;
        continue;
      }

      if (!isTransientUploadStatus(response.status) || attempt === DIRECT_UPLOAD_MAX_RETRIES) {
        throw new Error('AVATAR_UPLOAD_FAILED');
      }
    } catch (error) {
      if (attempt === DIRECT_UPLOAD_MAX_RETRIES) {
        throw error;
      }
    }

    await delay(300 * (attempt + 1));
  }

  throw new Error('AVATAR_UPLOAD_FAILED');
}

async function requestAvatarUploadUrl(userId: number, jpegBlob: Blob): Promise<AvatarUploadUrlResponse> {
  const response = await API.post(`/profile/${userId}/avatar/upload-url`, {
    contentType: AVATAR_CONTENT_TYPE,
    byteSize: jpegBlob.size,
    width: AVATAR_SIZE_PX,
    height: AVATAR_SIZE_PX,
  });
  return JSON.parse(response);
}

async function completeAvatarUpload(userId: number, uploadToken: string): Promise<{ avatarUrl: string }> {
  const response = await API.post(`/profile/${userId}/avatar/complete`, { uploadToken });
  return JSON.parse(response);
}

async function uploadAvatarViaBackend(userId: number, file: File): Promise<{ avatarUrl: string }> {
  const data = new FormData();
  data.append('file', file);
  const response = await API.upload(`/profile/${userId}/avatar`, data);
  return JSON.parse(response);
}

async function normalizeAvatarImage(file: File): Promise<Blob> {
  if (!isSupportedAvatarSource(file)) {
    throw new Error('AVATAR_TYPE_NOT_SUPPORTED');
  }

  const sourceBlob = isHeicImage(file) ? await convertHeicToJpeg(file) : file;
  const objectUrl = URL.createObjectURL(sourceBlob);
  try {
    const image = await loadImage(objectUrl);
    if (!image.naturalWidth || !image.naturalHeight) {
      throw new Error('AVATAR_TYPE_NOT_SUPPORTED');
    }

    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_SIZE_PX;
    canvas.height = AVATAR_SIZE_PX;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('AVATAR_UPLOAD_FAILED');
    }

    const cropSize = Math.min(image.naturalWidth, image.naturalHeight);
    const cropX = (image.naturalWidth - cropSize) / 2;
    const cropY = (image.naturalHeight - cropSize) / 2;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, AVATAR_SIZE_PX, AVATAR_SIZE_PX);
    context.drawImage(
      image,
      cropX,
      cropY,
      cropSize,
      cropSize,
      0,
      0,
      AVATAR_SIZE_PX,
      AVATAR_SIZE_PX
    );

    const blob = await canvasToBlob(canvas);
    if (blob.size > MAX_AVATAR_FILE_SIZE_BYTES) {
      throw new Error('AVATAR_TOO_LARGE');
    }
    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('AVATAR_TYPE_NOT_SUPPORTED'));
    image.src = url;
  });
}

async function convertHeicToJpeg(file: File): Promise<Blob> {
  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({
    blob: file,
    toType: AVATAR_CONTENT_TYPE,
    quality: AVATAR_JPEG_QUALITY,
  });

  return Array.isArray(converted) ? converted[0] : converted;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('AVATAR_UPLOAD_FAILED'));
        return;
      }
      resolve(blob);
    }, AVATAR_CONTENT_TYPE, AVATAR_JPEG_QUALITY);
  });
}

function isTransientUploadStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function isSupportedAvatarSource(file: File): boolean {
  return SUPPORTED_AVATAR_SOURCE_TYPES.includes(file.type) || isHeicImage(file);
}

function isHeicImage(file: File): boolean {
  const fileName = file.name.toLowerCase();
  return file.type === 'image/heic'
    || file.type === 'image/heif'
    || fileName.endsWith('.heic')
    || fileName.endsWith('.heif');
}

function fromUser(user: User): ProfileFormState {
  const birthDateParts = fromUserBirthDate(user);
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    birthMonth: birthDateParts.birthMonth,
    birthDay: birthDateParts.birthDay,
    birthYear: birthDateParts.birthYear,
    gender: user?.gender || '',
  };
}

function normalizeProfileFieldValue(name: string, value: string): string {
  if (name === 'birthDay') {
    return value.replace(/\D/g, '').slice(0, 2);
  }

  if (name === 'birthYear') {
    return value.replace(/\D/g, '').slice(0, 4);
  }

  return value;
}

function validateProfileNames(form: ProfileFormState): string | null {
  if (countNonWhitespaceCharacters(form.firstName) < 2) {
    return 'First name must contain at least 2 characters.';
  }

  if (countNonWhitespaceCharacters(form.lastName) < 2) {
    return 'Last name must contain at least 2 characters.';
  }

  return null;
}

function collapseInnerWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function countNonWhitespaceCharacters(value: string): number {
  return Array.from(value).filter((character) => character.trim() !== '').length;
}

function fromUserBirthDate(user?: User): Pick<ProfileFormState, 'birthMonth' | 'birthDay' | 'birthYear'> {
  if (user?.birthMonth && user?.birthDay) {
    return {
      birthMonth: String(user.birthMonth),
      birthDay: String(user.birthDay),
      birthYear: user.birthYear ? String(user.birthYear) : '',
    };
  }

  return { birthMonth: '', birthDay: '', birthYear: '' };
}

function toBirthDatePayload(form: ProfileFormState): {
  isValid: boolean;
  error: string;
  birthMonth: number | null;
  birthDay: number | null;
  birthYear: number | null;
} {
  const hasBirthMonth = form.birthMonth !== '';
  const hasBirthDay = form.birthDay !== '';
  const hasBirthYear = form.birthYear !== '';
  const birthMonth = hasBirthMonth ? Number(form.birthMonth) : null;
  const birthDay = hasBirthDay ? Number(form.birthDay) : null;
  const birthYear = hasBirthYear ? Number(form.birthYear) : null;

  if (!hasBirthMonth && !hasBirthDay && !hasBirthYear) {
    return { isValid: true, error: '', birthMonth: null, birthDay: null, birthYear: null };
  }

  if (!hasBirthMonth || !hasBirthDay) {
    return {
      isValid: false,
      error: 'Enter both month and day for your birthday.',
      birthMonth: null,
      birthDay: null,
      birthYear: null,
    };
  }

  const year = birthYear !== null ? birthYear : 2000;
  if (!isValidBirthDate(year, birthMonth, birthDay)) {
    return {
      isValid: false,
      error: 'Enter a valid birthday.',
      birthMonth: null,
      birthDay: null,
      birthYear: null,
    };
  }

  if (birthYear && (birthYear < MIN_BIRTH_YEAR || birthYear > new Date().getFullYear())) {
    return {
      isValid: false,
      error: 'Enter a valid birth year.',
      birthMonth: null,
      birthDay: null,
      birthYear: null,
    };
  }

  return {
    isValid: true,
    error: '',
    birthMonth,
    birthDay,
    birthYear,
  };
}

function isValidBirthDate(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function hasPhoneChanged(nextPhoneNumber: string, originalPhoneNumber: string): boolean {
  return nextPhoneNumber.trim() !== originalPhoneNumber.trim();
}

function hasEmailChanged(nextEmail: string, originalEmail: string): boolean {
  return nextEmail.trim().toLowerCase() !== originalEmail.trim().toLowerCase();
}

function toProfileError(error: any): string {
  const message = error?.message || error?.error || error;
  if (error?.status === 413) {
    return AVATAR_SOURCE_TOO_LARGE_MESSAGE;
  }

  switch (message) {
    case 'INVALID_FIRST_NAME':
      return 'First name must contain at least 2 characters.';
    case 'INVALID_LAST_NAME':
      return 'Last name must contain at least 2 characters.';
    case 'EMAIL_ALREADY_EXISTS':
      return 'This email is already used by another user.';
    case 'INVALID_EMAIL':
      return 'Enter a valid email address.';
    case 'EMAIL_REQUIRED':
      return 'Enter your email address.';
    case 'EMAIL_VERIFICATION_REQUIRED':
      return 'This email change needs verification. Click Verify, enter the 6-digit code, then save again.';
    case 'INVALID_EMAIL_VERIFICATION_CODE':
      return 'The email verification code is invalid or expired.';
    case 'THROTTLING_EMAIL':
      return 'Please wait before requesting another email verification code.';
    case 'PHONE_ALREADY_EXISTS':
      return 'This phone number is already used by another user.';
    case 'INVALID_PHONE_NUMBER':
      return 'Enter a valid phone number.';
    case 'PHONE_VERIFICATION_REQUIRED':
      return 'This phone number change needs verification. Click Verify, enter the 6-digit code, then save again.';
    case 'INVALID_PHONE_VERIFICATION_CODE':
      return 'The verification code is invalid or expired.';
    case 'THROTTLING_PHONE':
      return 'Please wait before requesting another verification code.';
    case 'INVALID_GENDER':
      return 'Choose a valid gender.';
    case 'INVALID_BIRTH_DATE':
      return 'Enter a valid birthday.';
    case 'AVATAR_TOO_LARGE':
      return AVATAR_TOO_LARGE_MESSAGE;
    case 'AVATAR_TYPE_NOT_SUPPORTED':
      return 'Use a PNG or JPG image.';
    case 'AVATAR_UPLOAD_EXPIRED':
    case 'AVATAR_UPLOAD_FAILED':
    case 'AVATAR_UPLOAD_NOT_FOUND':
      return 'Profile picture could not be uploaded. Please try again.';
    default:
      return 'Profile could not be updated.';
  }
}

export default UserProfileModal;
